import { Badge, Button, Card, LoadingContainer } from '@/components/ui';
import {
  sysadminService,
  type AudiosDuplicadosResponse,
  type EmpresaAudiosDuplicadosDTO,
  type GrupoAudiosDuplicadosDTO,
} from '@/services/sysadmin.service';
import {
  AlertTriangle,
  ArrowRight,
  Clock,
  Download,
  ExternalLink,
  FileSpreadsheet,
  Layers,
  Music,
  RefreshCw,
  Search,
  Sparkles,
  Users,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export function AudiosDuplicadosPanel() {
  const [data, setData] = useState<AudiosDuplicadosResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const [viewMode, setViewMode] = useState<'empresa' | 'grupo' | 'caso2'>('empresa');
  const [searchTerm, setSearchTerm] = useState('');
  const [minHoursFilter, setMinHoursFilter] = useState<number>(0);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await sysadminService.obtenerAudiosDuplicados();
      setData(res);
    } catch (err: any) {
      setError(err.message || 'Error al cargar reporte de audios duplicados');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleExportExcel = async () => {
    setIsExporting(true);
    try {
      const blob = await sysadminService.descargarExcelAudiosDuplicados();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audios_duplicados_por_empresa_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Error al exportar archivo Excel');
    } finally {
      setIsExporting(false);
    }
  };

  // Filtrado de empresas
  const filteredEmpresas = useMemo(() => {
    if (!data?.empresas) return [];
    return data.empresas.filter((emp: EmpresaAudiosDuplicadosDTO) => {
      const matchSearch =
        searchTerm === '' ||
        emp.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.nit.includes(searchTerm) ||
        emp.asesorTitular.toLowerCase().includes(searchTerm.toLowerCase());

      const matchHours = minHoursFilter === 0 || emp.audios.some((a) => a.duracionSeg >= minHoursFilter * 3600);

      return matchSearch && matchHours;
    });
  }, [data?.empresas, searchTerm, minHoursFilter]);

  // Filtrado de grupos
  const filteredGrupos = useMemo(() => {
    if (!data?.grupos) return [];
    return data.grupos.filter((grp: GrupoAudiosDuplicadosDTO) => {
      const matchHours = minHoursFilter === 0 || grp.duracionSeg >= minHoursFilter * 3600;

      const matchSearch =
        searchTerm === '' ||
        grp.audios.some(
          (a) =>
            a.empresaNombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
            a.empresaId.includes(searchTerm) ||
            a.asesorNombre.toLowerCase().includes(searchTerm.toLowerCase())
        );

      return matchHours && matchSearch;
    });
  }, [data?.grupos, searchTerm, minHoursFilter]);

  if (isLoading) {
    return <LoadingContainer text="Cargando análisis de audios coincidentes..." />;
  }

  if (error || !data) {
    return (
      <Card className="p-8 text-center text-red-600 bg-red-50 border-red-200">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-red-500" />
        <p className="font-semibold text-sm mb-4">{error || 'No se pudieron cargar los datos'}</p>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reintentar
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Header con Resumen y Botón de Exportación ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl border border-gray-200 shadow-xs">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-base font-bold text-gray-900">Auditoría de Audios Repetidos y Duplicados</h2>
            <Badge variant="warning" className="text-[11px] font-semibold">
              Solo Sysadmin
            </Badge>
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            Grabaciones con duraciones idénticas al segundo entre diferentes empresas y asesores en producción.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button onClick={fetchData} variant="outline" size="sm" className="text-xs text-gray-700 hover:bg-gray-50" disabled={isLoading}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>

          <Button
            onClick={handleExportExcel}
            disabled={isExporting}
            className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs shadow-xs px-4 py-2 flex items-center gap-2"
          >
            {isExporting ? (
              <>
                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Generando Excel...
              </>
            ) : (
              <>
                <FileSpreadsheet className="w-4 h-4" />
                Exportar Excel (.xlsx)
              </>
            )}
          </Button>
        </div>
      </div>

      {/* ── Tarjetas de Métricas ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4 bg-blue-50/50 border-blue-200">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider font-bold text-blue-700">Total Audios en BD</span>
            <Music className="w-4 h-4 text-blue-600" />
          </div>
          <p className="text-2xl font-black text-blue-950 mt-1">{data.resumen.totalAudios}</p>
          <span className="text-[10px] text-blue-600/80">Audios registrados</span>
        </Card>

        <Card className="p-4 bg-amber-50/50 border-amber-200">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider font-bold text-amber-700">Grupos Coincidentes</span>
            <Layers className="w-4 h-4 text-amber-600" />
          </div>
          <p className="text-2xl font-black text-amber-950 mt-1">{data.resumen.totalGrupos}</p>
          <span className="text-[10px] text-amber-600/80">Duraciones repetidas</span>
        </Card>

        <Card className="p-4 bg-rose-50/50 border-rose-200">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider font-bold text-rose-700">Audios con Clonación</span>
            <AlertTriangle className="w-4 h-4 text-rose-600" />
          </div>
          <p className="text-2xl font-black text-rose-950 mt-1">{data.resumen.totalAudiosCoincidentes}</p>
          <span className="text-[10px] text-rose-600/80">Audios en grupos idénticos</span>
        </Card>

        <Card className="p-4 bg-purple-50/50 border-purple-200">
          <div className="flex items-center justify-between">
            <span className="text-[11px] uppercase tracking-wider font-bold text-purple-700">Empresas Afectadas</span>
            <Users className="w-4 h-4 text-purple-600" />
          </div>
          <p className="text-2xl font-black text-purple-950 mt-1">{data.resumen.totalEmpresasAfectadas}</p>
          <span className="text-[10px] text-purple-600/80">Empresas con audios repetidos</span>
        </Card>
      </div>

      {/* ── Controles de Filtros y Modo de Vista ── */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3">
        {/* Toggle de Vistas */}
        <div className="flex bg-gray-100 p-1 rounded-lg border border-gray-200">
          <button
            type="button"
            onClick={() => setViewMode('empresa')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer ${
              viewMode === 'empresa' ? 'bg-white text-gray-900 shadow-xs font-bold' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Agrupado por Empresa ({filteredEmpresas.length})
          </button>
          <button
            type="button"
            onClick={() => setViewMode('grupo')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer ${
              viewMode === 'grupo' ? 'bg-white text-gray-900 shadow-xs font-bold' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Por Grupo de Duración ({filteredGrupos.length})
          </button>
          <button
            type="button"
            onClick={() => setViewMode('caso2')}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all cursor-pointer flex items-center gap-1 ${
              viewMode === 'caso2'
                ? 'bg-white text-amber-900 shadow-xs font-bold border border-amber-300'
                : 'text-amber-700 hover:text-amber-900'
            }`}
          >
            <Sparkles className="w-3 h-3 text-amber-500" />
            Caso Asesoría 2 vs 26
          </button>
        </div>

        {/* Buscador y filtro de duración */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por empresa, NIT o asesor..."
              className="pl-8 pr-3 py-1.5 border border-gray-200 rounded-lg text-xs w-64 focus:outline-2 focus:outline-gray-900"
            />
          </div>

          <select
            value={minHoursFilter}
            onChange={(e) => setMinHoursFilter(Number(e.target.value))}
            className="border border-gray-200 rounded-lg text-xs px-2.5 py-1.5 bg-white text-gray-700 focus:outline-2 focus:outline-gray-900"
          >
            <option value={0}>Todas las duraciones</option>
            <option value={1}>Mayor a 1 hora</option>
            <option value={3}>Mayor a 3 horas</option>
            <option value={6}>Mayor a 6 horas</option>
          </select>
        </div>
      </div>

      {/* ── Vista 1: Agrupado por Empresa ── */}
      {viewMode === 'empresa' && (
        <div className="space-y-4">
          {filteredEmpresas.length === 0 ? (
            <Card className="p-8 text-center text-gray-500 text-xs">No se encontraron empresas con los filtros aplicados.</Card>
          ) : (
            filteredEmpresas.map((emp) => (
              <Card key={emp.nit} className="overflow-hidden border-gray-200">
                {/* Cabecera de la Empresa */}
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-gray-900">{emp.nombre}</span>
                      <span className="text-[11px] font-mono text-gray-500">NIT: {emp.nit}</span>
                    </div>
                    <span className="text-[11px] text-gray-500">
                      Asesor: <strong>{emp.asesorTitular}</strong>
                    </span>
                  </div>
                  <Badge variant="warning" className="text-[11px] self-start md:self-auto">
                    {emp.audios.length} audio(s) con coincidencias
                  </Badge>
                </div>

                {/* Tabla de audios de la empresa */}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-gray-100/60 text-gray-600 font-semibold border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 w-20">Audio ID</th>
                        <th className="px-3 py-2 w-24">Asesoría</th>
                        <th className="px-3 py-2 w-28">Fecha</th>
                        <th className="px-3 py-2 w-32">Duración</th>
                        <th className="px-3 py-2 w-32">Google Drive</th>
                        <th className="px-3 py-2">Empresas / Asesores con Duración Exacta</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {emp.audios.map((audio) => (
                        <tr key={audio.audioId} className="hover:bg-amber-50/20">
                          <td className="px-3 py-2.5 font-bold text-gray-900">#{audio.audioId}</td>
                          <td className="px-3 py-2.5 text-gray-600 font-medium">#{audio.asesoriaId}</td>
                          <td className="px-3 py-2.5 text-gray-500">{new Date(audio.fechaGrabacion).toISOString().slice(0, 10)}</td>
                          <td className="px-3 py-2.5">
                            <span className="font-mono font-semibold text-gray-900">{audio.duracionFormatted}</span>
                          </td>
                          <td className="px-3 py-2.5">
                            <a
                              href={audio.driveUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1 hover:underline"
                            >
                              <span>Escuchar</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="px-3 py-2.5">
                            {audio.coincidencias.length === 0 ? (
                              <span className="text-gray-400 italic">Sin duplicados</span>
                            ) : (
                              <div className="space-y-1">
                                {audio.coincidencias.map((c, i) => (
                                  <div
                                    key={i}
                                    className="flex items-center justify-between bg-amber-50/80 border border-amber-200/60 rounded px-2 py-1 text-[11px]"
                                  >
                                    <div className="flex items-center gap-1.5 text-amber-950">
                                      <span className="font-bold">Audio #{c.audioId}</span>
                                      <span>en</span>
                                      <span className="font-semibold text-amber-900">{c.empresaNombre}</span>
                                      <span className="text-gray-500">(Asesor: {c.asesorNombre})</span>
                                    </div>
                                    <a
                                      href={c.driveUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1 hover:underline ml-2 shrink-0"
                                    >
                                      <span>Comparar</span>
                                      <ExternalLink className="w-2.5 h-2.5" />
                                    </a>
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* ── Vista 2: Por Grupo de Duración ── */}
      {viewMode === 'grupo' && (
        <div className="space-y-4">
          {filteredGrupos.length === 0 ? (
            <Card className="p-8 text-center text-gray-500 text-xs">No se encontraron grupos con los filtros aplicados.</Card>
          ) : (
            filteredGrupos.map((grp, gIdx) => (
              <Card key={gIdx} className="overflow-hidden border-gray-200">
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="font-bold text-xs text-gray-900">Duración Exacta: {grp.duracionFormatted}</span>
                    <span className="text-[11px] font-mono text-gray-500">({grp.duracionSeg} segundos)</span>
                  </div>
                  <Badge variant="warning" className="text-[11px]">
                    {grp.totalAudios} audios idénticos
                  </Badge>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-gray-100/50 text-gray-600 font-semibold border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2">Audio ID</th>
                        <th className="px-3 py-2">Empresa</th>
                        <th className="px-3 py-2">NIT</th>
                        <th className="px-3 py-2">Asesor</th>
                        <th className="px-3 py-2">Asesoría</th>
                        <th className="px-3 py-2">Fecha Grabación</th>
                        <th className="px-3 py-2">Google Drive</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {grp.audios.map((a) => (
                        <tr key={a.audioId} className="hover:bg-blue-50/20">
                          <td className="px-3 py-2 font-bold text-gray-900">#{a.audioId}</td>
                          <td className="px-3 py-2 font-medium text-gray-900">{a.empresaNombre}</td>
                          <td className="px-3 py-2 font-mono text-gray-500">{a.empresaId}</td>
                          <td className="px-3 py-2 text-gray-700">{a.asesorNombre}</td>
                          <td className="px-3 py-2 text-gray-600 font-medium">#{a.asesoriaId}</td>
                          <td className="px-3 py-2 text-gray-500">{new Date(a.fechaGrabacion).toISOString().slice(0, 10)}</td>
                          <td className="px-3 py-2">
                            <a
                              href={a.driveUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1 hover:underline"
                            >
                              <span>Escuchar en Drive</span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* ── Vista 3: Caso Especial Asesoría 2 vs 26 ── */}
      {viewMode === 'caso2' && (
        <div className="space-y-6">
          <Card className="p-5 border-amber-300 bg-amber-50/40">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-bold text-amber-950">
                  Cotejo Especial: Asesoría #2 (Ecoaventura guayabetal) vs Asesoría #26 (Contacto extremo)
                </h3>
                <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                  Las 4 grabaciones iniciales de la Asesoría #2 (Asesor: <strong>DALI BAUTISTA LEON</strong>) coinciden al segundo exacto
                  con las 4 grabaciones de la Asesoría #26 (Asesora: <strong>ANGIE CATALINA BAUTISTA ARDILA</strong>). Haz clic en los
                  enlaces para abrir ambos audios en paralelo y confirmar si el archivo de audio es el mismo.
                </p>
              </div>
            </div>
          </Card>

          {/* Pares clonados */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700">4 Pares con Duración Idéntica al Segundo Exacto:</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.casoEspecial.paresClonados.map((par) => (
                <Card key={par.parNum} className="p-4 border-amber-200 bg-white">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-3">
                    <span className="font-bold text-xs text-amber-900">Par #{par.parNum}</span>
                    <Badge variant="warning" className="font-mono text-[11px]">
                      {par.duracionFormatted}
                    </Badge>
                  </div>

                  <div className="space-y-3 text-xs">
                    {/* Audio A */}
                    {par.audioA && (
                      <div className="p-2.5 rounded-lg bg-blue-50/50 border border-blue-100">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-blue-950">Audio #{par.audioA.audioId}</span>
                          <span className="text-[10px] text-blue-600">Asesoría #{par.audioA.asesoriaId}</span>
                        </div>
                        <div className="text-gray-700 font-medium mt-0.5">{par.audioA.empresa}</div>
                        <div className="text-gray-500 text-[11px]">Asesor: {par.audioA.asesor}</div>
                        <a
                          href={par.audioA.driveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-semibold hover:underline"
                        >
                          <span>Abrir Audio en Drive</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}

                    {/* Audio B */}
                    {par.audioB && (
                      <div className="p-2.5 rounded-lg bg-emerald-50/50 border border-emerald-100">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-emerald-950">Audio #{par.audioB.audioId}</span>
                          <span className="text-[10px] text-emerald-600">Asesoría #{par.audioB.asesoriaId}</span>
                        </div>
                        <div className="text-gray-700 font-medium mt-0.5">{par.audioB.empresa}</div>
                        <div className="text-gray-500 text-[11px]">Asesor: {par.audioB.asesor}</div>
                        <a
                          href={par.audioB.driveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-800 font-semibold hover:underline"
                        >
                          <span>Abrir Audio en Drive</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* 6 audios de jornada completa */}
          <div className="space-y-3 pt-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700">
              Los 6 Audios Posteriores de Jornada Completa (Asesoría #2 — Ecoaventura guayabetal):
            </h4>
            <Card className="overflow-hidden border-gray-200">
              <table className="w-full text-xs text-left">
                <thead className="bg-gray-50 text-gray-600 font-semibold border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2">Audio ID</th>
                    <th className="px-3 py-2">Duración</th>
                    <th className="px-3 py-2">Segundos Exactos</th>
                    <th className="px-3 py-2">Fecha Grabación</th>
                    <th className="px-3 py-2">Google Drive</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.casoEspecial.jornadaCompletaA2.map((j) => (
                    <tr key={j.audioId} className="hover:bg-blue-50/20">
                      <td className="px-3 py-2 font-bold text-gray-900">#{j.audioId}</td>
                      <td className="px-3 py-2 font-mono font-semibold text-gray-900">{j.duracionFormatted}</td>
                      <td className="px-3 py-2 font-mono text-gray-500">{j.duracionSeg} s</td>
                      <td className="px-3 py-2 text-gray-600">{new Date(j.fechaGrabacion).toISOString().slice(0, 10)}</td>
                      <td className="px-3 py-2">
                        <a
                          href={j.driveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1 hover:underline"
                        >
                          <span>Escuchar en Drive</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
